import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { format, differenceInMinutes } from 'date-fns';
import { getPositionAndWidth } from '@/lib/scheduling/positioning-compatibility-layer';
import { getZIndexClass } from '@/lib/scheduling/z-index-layers';
import { useShiftResize } from '@/hooks/scheduling/useShiftResize';
import { EDITING_COLORS, EDITING_COLORS_CSS } from '@/lib/scheduling/editing-colors';

const INTERVALS_PER_HOUR = 4;

// Format duration helper - moved outside component to avoid recreation on each render
const formatDuration = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

interface ShiftCreationOverlayProps {
  startTime: Date | null;
  endTime: Date | null;
  effectiveDisplayStartTime: Date;
  effectiveDisplayEndTime: Date;
  totalDisplayHours: number;
  calculatedHourCellWidthPx: number;
  rowHeightPx: number;
  intervalMinutes: number;
  minutesPerHour: number;
  targetRowIndex?: number | null;
  targetDayVerticalOffset?: number;
  dayColumnOffset?: number;
  isResizeMode?: boolean;
  onResizeComplete?: (startTime: Date, endTime: Date) => void;
  onFinalizedClick?: () => void;
}

const ShiftCreationOverlay: React.FC<ShiftCreationOverlayProps> = ({
  startTime,
  endTime,
  effectiveDisplayStartTime,
  effectiveDisplayEndTime,
  totalDisplayHours,
  calculatedHourCellWidthPx,
  rowHeightPx,
  intervalMinutes,
  minutesPerHour,
  targetRowIndex,
  targetDayVerticalOffset = 0,
  dayColumnOffset = 32,
  isResizeMode = false,
  onResizeComplete,
  onFinalizedClick,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [localStartTime, setLocalStartTime] = useState<Date | null>(startTime);
  const [localEndTime, setLocalEndTime] = useState<Date | null>(endTime);
  const [measuredContainerWidth, setMeasuredContainerWidth] = useState<number>(0);
  const lastStableWidthRef = useRef<number>(0); // Track last stable width to prevent micro-fluctuations

  // Track overlay position for portal tooltips
  const [overlayRect, setOverlayRect] = useState<DOMRect | null>(null);

  // Measure container width for accurate cell width calculation
  useEffect(() => {
    const container = overlayRef.current?.closest('.virtualized-day-item') || overlayRef.current?.parentElement;
    if (!container) return;

    const measureWidth = () => {
      const width = Math.round(container.getBoundingClientRect().width);
      // Only update if change is significant (> 2px) to prevent width "bouncing"
      const widthDiff = Math.abs(width - lastStableWidthRef.current);
      if (widthDiff > 2 || lastStableWidthRef.current === 0) {
        lastStableWidthRef.current = width;
        setMeasuredContainerWidth(width);
      }
    };

    // Initial measurement
    measureWidth();

    // Re-measure on resize
    const resizeObserver = new ResizeObserver(measureWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Track overlay position for portal tooltips
  useEffect(() => {
    if (!overlayRef.current) return;

    const updateRect = () => {
      if (overlayRef.current) {
        setOverlayRect(overlayRef.current.getBoundingClientRect());
      }
    };

    // Initial measurement
    updateRect();

    // Update on scroll and resize
    const scrollContainer = overlayRef.current.closest('.virtualized-calendar-list');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', updateRect);
    }
    window.addEventListener('resize', updateRect);

    // Use RAF for smooth updates during interactions
    const rafId = requestAnimationFrame(updateRect);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', updateRect);
      }
      window.removeEventListener('resize', updateRect);
      cancelAnimationFrame(rafId);
    };
  }, [startTime, endTime, localStartTime, localEndTime]);

  // Calculate actual cell width from measured container
  const actualCellWidth = measuredContainerWidth > 0 && totalDisplayHours > 0
    ? (measuredContainerWidth - dayColumnOffset) / (totalDisplayHours * INTERVALS_PER_HOUR)
    : calculatedHourCellWidthPx;

  // Sync local times with props
  useEffect(() => {
    setLocalStartTime(startTime);
    setLocalEndTime(endTime);
  }, [startTime, endTime]);

  // Use the robust resize hook for drag resize
  const { startResize } = useShiftResize({
    startTime: localStartTime || new Date(),
    endTime: localEndTime || new Date(),
    intervalWidthPx: actualCellWidth,
    minDurationMinutes: intervalMinutes,
    onResize: (newStart, newEnd) => {
      // Visual update during drag
      setLocalStartTime(newStart);
      setLocalEndTime(newEnd);
    },
    onResizeEnd: (newStart, newEnd) => {
      // Commit the change
      if (onResizeComplete) {
        onResizeComplete(newStart, newEnd);
      }
    },
  });

  if (!startTime) return null;

  const displayStart = localStartTime || startTime;
  const displayEnd = localEndTime || endTime;
  const top = targetDayVerticalOffset + (targetRowIndex != null ? targetRowIndex * rowHeightPx : 0);

  // Preview mode: only start time
  if (!endTime) {
    const { left } = getPositionAndWidth(
      startTime, startTime, effectiveDisplayStartTime, effectiveDisplayEndTime,
      totalDisplayHours, intervalMinutes, minutesPerHour, actualCellWidth
    );

    return (
      <>
        <div
          ref={overlayRef}
          className={`absolute pointer-events-none ${getZIndexClass('CREATION_OVERLAY')}`}
          style={{ left: `${left}px`, width: '4px', height: `${rowHeightPx}px`, top: `${top}px`, backgroundColor: EDITING_COLORS_CSS.BG_MEDIUM, borderRadius: '2px' }}
        />
        {/* Portal-based tooltip to avoid overflow issues */}
        {overlayRect && createPortal(
          <div
            className={`fixed ${EDITING_COLORS.BG_PROMINENT} text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap pointer-events-none border ${EDITING_COLORS.BORDER_MEDIUM} z-[9999]`}
            style={{
              left: `${overlayRect.left - 32}px`,
              top: `${overlayRect.top - 24}px`,
            }}
          >
            Start: {format(startTime, 'HH:mm')}
          </div>,
          document.body
        )}
      </>
    );
  }

  // Full overlay
  const { left, width } = getPositionAndWidth(
    displayStart, displayEnd, effectiveDisplayStartTime, effectiveDisplayEndTime,
    totalDisplayHours, intervalMinutes, minutesPerHour, actualCellWidth
  );

  const duration = differenceInMinutes(displayEnd, displayStart);

  const handleCenterClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onFinalizedClick?.();
  };

  return (
    <>
      <div
        ref={overlayRef}
        className={`absolute pointer-events-none ${getZIndexClass('CREATION_OVERLAY')}`}
        style={{
          left: `${left}px`,
          width: `${width}px`,
          height: `${rowHeightPx}px`,
          top: `${top}px`,
          backgroundColor: EDITING_COLORS_CSS.BG_OVERLAY,
          border: `2px solid ${EDITING_COLORS_CSS.BORDER_MEDIUM}`,
          borderRadius: '4px',
        }}
        data-shift-overlay="true"
      >
        {/* Center area / instructions (inside overlay when wide enough) */}
        {isResizeMode ? (
          <div className="absolute top-0 bottom-0 left-3 right-3 flex items-center justify-center pointer-events-auto cursor-pointer" onClick={handleCenterClick}>
            {width >= 200 && (
              <div className={`${EDITING_COLORS.BG_PROMINENT} text-white text-xs font-medium px-2 py-1 mx-4 rounded shadow-md border ${EDITING_COLORS.BORDER_MEDIUM} ${EDITING_COLORS.HOVER_PROMINENT} transition-colors text-center line-clamp-2`}>
                Drag edges to resize or click to create shift
              </div>
            )}
          </div>
        ) : (
          width >= 100 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className={`${EDITING_COLORS.BG_PROMINENT} text-white text-xs font-medium px-2 py-1 rounded shadow-sm border ${EDITING_COLORS.BORDER_MEDIUM} whitespace-nowrap`}>
                Click end time
              </div>
            </div>
          )
        )}

        {/* Resize handles */}
        {isResizeMode && (
          <>
            <div
              className="absolute left-0 top-0 h-full w-3 cursor-col-resize pointer-events-auto flex items-center justify-center z-10 transition-colors bg-primary/20 hover:bg-primary/40 active:bg-primary/50"
              onPointerDown={(e) => startResize(e, 'left')}
            >
              <div className="w-1 h-full bg-primary" />
            </div>
            <div
              className="absolute right-0 top-0 h-full w-3 cursor-col-resize pointer-events-auto flex items-center justify-center z-10 transition-colors bg-primary/20 hover:bg-primary/40 active:bg-primary/50"
              onPointerDown={(e) => startResize(e, 'right')}
            >
              <div className="w-1 h-full bg-primary" />
            </div>
          </>
        )}
      </div>

      {/* Portal-based tooltips to avoid overflow clipping */}
      {overlayRect && createPortal(
        <>
          {/* Time tooltip - positioned above overlay */}
          <div
            className={`fixed ${EDITING_COLORS.BG_PROMINENT} text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap pointer-events-none border ${EDITING_COLORS.BORDER_MEDIUM} z-[9999]`}
            style={{
              left: `${overlayRect.left + overlayRect.width / 2}px`,
              top: `${overlayRect.top - 56}px`,
              transform: 'translateX(-50%)',
            }}
          >
            <div className="text-center">
              <div className="font-medium">{format(displayStart, 'HH:mm')} - {format(displayEnd, 'HH:mm')}</div>
              <div className="text-white/80 mt-1">Duration: {formatDuration(duration)}</div>
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-primary/85" />
          </div>

          {/* Instructions tooltip - positioned below overlay when narrow */}
          {isResizeMode && width < 200 && (
            <div
              className={`fixed ${EDITING_COLORS.BG_PROMINENT} text-white text-xs font-medium px-2 py-1 rounded shadow-md border ${EDITING_COLORS.BORDER_MEDIUM} whitespace-nowrap pointer-events-auto cursor-pointer ${EDITING_COLORS.HOVER_PROMINENT} transition-colors z-[9999]`}
              style={{
                left: `${overlayRect.left + overlayRect.width / 2}px`,
                top: `${overlayRect.bottom + 8}px`,
                transform: 'translateX(-50%)',
              }}
              onClick={handleCenterClick}
            >
              Drag edges to resize or click to create
            </div>
          )}

          {/* Non-resize mode instructions when narrow */}
          {!isResizeMode && width < 100 && (
            <div
              className={`fixed ${EDITING_COLORS.BG_PROMINENT} text-white text-xs font-medium px-2 py-1 rounded shadow-sm border ${EDITING_COLORS.BORDER_MEDIUM} whitespace-nowrap pointer-events-none z-[9999]`}
              style={{
                left: `${overlayRect.left + overlayRect.width / 2}px`,
                top: `${overlayRect.bottom + 8}px`,
                transform: 'translateX(-50%)',
              }}
            >
              Click end time
            </div>
          )}
        </>,
        document.body
      )}
    </>
  );
};

export default ShiftCreationOverlay;
